import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography, Button, IconButton,
  Drawer, List, ListItem, ListItemText, Box, Container,
  useScrollTrigger, Slide, useMediaQuery, useTheme, Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Our Properties', to: '/properties' },
  { label: 'Rooms & Suites', to: '/rooms' },
  { label: 'Amenities', to: '/amenities' },
  { label: 'About Us', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

function HideOnScroll({ children }) {
  const trigger = useScrollTrigger();
  return <Slide appear={false} direction="down" in={!trigger}>{children}</Slide>;
}

export default function Header({ property }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const isActive = (to) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Top bar */}
      <Box
        sx={{
          background: 'linear-gradient(90deg, #C04A0A, #E55A25)',
          color: '#fff',
          py: 0.5,
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            {property?.phones?.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.85)' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  {property.phones.map((p, i) => (
                    <React.Fragment key={p.id}>
                      <Typography
                        component="a"
                        href={`tel:${p.phone}`}
                        variant="caption"
                        sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#FFD700' } }}
                      >
                        {p.phone}
                      </Typography>
                      {i < property.phones.length - 1 && ', '}
                    </React.Fragment>
                  ))}
                </Typography>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      <HideOnScroll>
        <AppBar
          position="sticky"
          elevation={scrolled ? 4 : 0}
          sx={{
            background: '#FFFFFF',
            borderBottom: '1px solid #FFD9C0',
            transition: 'all 0.3s ease',
          }}
        >
          <Container maxWidth="lg">
            <Toolbar sx={{ py: 1, px: '0 !important' }}>
              {/* Logo */}
              <Box
                component={Link}
                to="/"
                sx={{ textDecoration: 'none', flexGrow: isMobile ? 1 : 0, mr: 4 }}
              >
                <img
                  src="/logo-reference-no-background.png"
                  alt="Preksha Hospitality"
                  style={{ height: 54, width: 'auto', display: 'block' }}
                />
              </Box>

              {/* Desktop Nav */}
              {!isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexGrow: 1 }}>
                  {NAV_LINKS.map((link) => (
                    <Button
                      key={link.to}
                      component={Link}
                      to={link.to}
                      sx={{
                        color: isActive(link.to) ? '#FF6B35' : '#2C1810',
                        fontSize: '0.75rem',
                        letterSpacing: '0.1em',
                        fontWeight: isActive(link.to) ? 700 : 400,
                        px: 1.5,
                        py: 0.75,
                        textTransform: 'uppercase',
                        borderBottom: isActive(link.to)
                          ? '2px solid #FF6B35'
                          : '2px solid transparent',
                        borderRadius: 0,
                        '&:hover': {
                          color: '#FF6B35',
                          backgroundColor: 'rgba(255,107,53,0.08)',
                          borderBottom: '2px solid #FF6B35',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {link.label}
                    </Button>
                  ))}
                </Box>
              )}

              {/* Book Now CTA */}
              {!isMobile && (
                <Button
                  component={Link}
                  to="/booking"
                  variant="contained"
                  color="primary"
                  sx={{ ml: 2, whiteSpace: 'nowrap', fontSize: '0.75rem' }}
                >
                  Book Now
                </Button>
              )}

              {/* Mobile Menu Icon */}
              {isMobile && (
                <IconButton
                  color="inherit"
                  onClick={() => setDrawerOpen(true)}
                  sx={{ color: '#FF6B35' }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            background: 'linear-gradient(180deg, #FF6B35 0%, #C84A1A 100%)',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <img
            src="/logo-reference-no-background.png"
            alt="Preksha Hospitality"
            style={{ height: 44, width: 'auto' }}
          />
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'rgba(255,255,255,0.85)' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,215,0,0.3)' }} />
        <List sx={{ pt: 2 }}>
          {NAV_LINKS.map((link) => (
            <ListItem
              key={link.to}
              component={Link}
              to={link.to}
              onClick={() => setDrawerOpen(false)}
              sx={{
                color: isActive(link.to) ? '#FFD700' : 'rgba(255,255,255,0.92)',
                borderLeft: isActive(link.to) ? '3px solid #FFD700' : '3px solid transparent',
                pl: 3,
                py: 1.5,
                '&:hover': { color: '#FFD700', backgroundColor: 'rgba(255,215,0,0.12)' },
              }}
            >
              <ListItemText
                primary={link.label}
                primaryTypographyProps={{
                  fontFamily: '"Lato", sans-serif',
                  fontSize: '0.9rem',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              />
            </ListItem>
          ))}
          <ListItem sx={{ px: 2, pt: 3 }}>
            <Button
              component={Link}
              to="/booking"
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => setDrawerOpen(false)}
            >
              Book Now
            </Button>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
}
